import { getAllMappings, LiveclassStudent } from "@oneapp/data";
import {
  findCountryMappingByIsdCode,
  findCountryMappingByTimezoneId,
} from "@repo/utils";
import { buildLiveclassStudentSyncPayload } from "../liveclassStudentSyncPayload";
import { useAuthStore, type CountryDetails } from "../../store/useAuthStore";
import {
  clearStoredLaunchCountryCode,
  setStoredLaunchCountryCode,
} from "../../store/launchCountryStore";
import {
  clearStoredGatewayCountryCode,
  setStoredGatewayCountryCode,
} from "../../store/gatewayCountryStore";
import {
  clearInfinityLearnHandoffCookies,
  readAccountsOfLoggedInUserCookie,
  readCountryInfoCookie,
  readInfinityLearnCookie,
  readProfilesOfLoggedInUserCookie,
} from "./cookies";
import { fetchEnrolledProgramsForHandoff } from "./fetchEnrolledPrograms";
import { mapCookiesToAuthState } from "./mapCookiesToAuthState";

const HANDOFF_LOG_PREFIX = "[IL Handoff]";

async function saveHandoffCountryDetails(
  timezoneId: number,
  isdCode?: string | null,
): Promise<void> {
  try {
    const mappings = await getAllMappings(
      { resource_type: "country" },
      { clientName: "masterData" },
    );
    const countryByIsd = findCountryMappingByIsdCode(mappings, isdCode);
    const countryTimezones = countryByIsd?.details.timezones;
    const isTimezoneInCountry =
      Array.isArray(countryTimezones) &&
      countryTimezones.some((timezone) => timezone.id === timezoneId);
    const country = isTimezoneInCountry
      ? countryByIsd
      : findCountryMappingByTimezoneId(mappings, timezoneId);

    if (!country) {
      console.warn(`${HANDOFF_LOG_PREFIX} No country mapping found.`, {
        timezoneId,
      });
      return;
    }

    useAuthStore
      .getState()
      .setCountryDetails(country as unknown as CountryDetails, timezoneId);
  } catch (error) {
    console.warn(`${HANDOFF_LOG_PREFIX} Country mapping fetch failed:`, error);
  }
}

/**
 * Hydrates {@link useAuthStore} from IL portal cookies when the user lands on oneapp
 * already logged in on the Infinity Learn website (IL profile, not SCA admission flow).
 */
export async function bootstrapInfinityLearnHandoff(): Promise<boolean> {
  const countryInfo = readCountryInfoCookie();

  const authState = useAuthStore.getState();
  const ilCookie = readInfinityLearnCookie();

  if (!ilCookie) {
    if (!authState.token) {
      clearStoredLaunchCountryCode();
      clearStoredGatewayCountryCode();
    }

    if (countryInfo) {
      setStoredLaunchCountryCode(countryInfo.countryCode);
      setStoredGatewayCountryCode(countryInfo.countryCode);
    }
    return false;
  }

  const parsedTimezoneId = Number(ilCookie.userDto.timezone_id);
  const dtoTimezoneId =
    Number.isFinite(parsedTimezoneId) && parsedTimezoneId > 0
      ? parsedTimezoneId
      : null;
  if (dtoTimezoneId !== null) {
    await saveHandoffCountryDetails(dtoTimezoneId, ilCookie.userDto.isd_code);
  }

  const { accessToken } = ilCookie;

  if (authState.token === accessToken && authState.profilesData) {
    clearInfinityLearnHandoffCookies();
    return true;
  }

  const accountsCookie = readAccountsOfLoggedInUserCookie();
  const profilesCookie = readProfilesOfLoggedInUserCookie();
  const mapped = mapCookiesToAuthState(
    ilCookie,
    accountsCookie,
    profilesCookie,
  );

  const mappedUserDetails =
    mapped.profilesData.unified_account_details?.user_account_details;
  if (dtoTimezoneId !== null && mappedUserDetails) {
    mappedUserDetails.timezone_id = dtoTimezoneId;
  }

  authState.setAccountsData(mapped.accountsData);
  authState.setProfilesData(mapped.profilesData);
  authState.setLoginMethod("phone_otp");

  try {
    const programs = await fetchEnrolledProgramsForHandoff(
      mapped.accessToken,
      mapped.profilesData,
    );
    if (programs.length > 0) {
      authState.setEnrolledPrograms(programs);
    } else {
      console.warn(
        `${HANDOFF_LOG_PREFIX} No enrolled programs returned; navigation may use defaults.`,
      );
    }
  } catch (error) {
    console.warn(
      `${HANDOFF_LOG_PREFIX} Enrolled programs fetch failed:`,
      error,
    );
  }

  authState.login(mapped.accessToken, mapped.user, undefined, true, {
    skipAinaIntroPrime: true,
  });

  try {
    const payload = buildLiveclassStudentSyncPayload();
    if (payload) {
      await LiveclassStudent.create({
        clientName: "liveclass",
        data: payload,
      });
    }
  } catch (error) {
    console.warn(`${HANDOFF_LOG_PREFIX} Liveclass student sync failed:`, error);
  }

  clearInfinityLearnHandoffCookies();

  console.info(
    `${HANDOFF_LOG_PREFIX} Session restored from INFINITY_LEARN cookie.`,
  );
  return true;
}
